import { useEffect, useState } from 'react';
import { FirestoreError } from 'firebase/firestore';
import { ColumnService } from 'lib/services/directory';
import { ERef, ESnapshotExists, exists } from '../../types';
import { getErrorReporter } from '../../utils/errors';

/**
 * Establish a live snapshot listener for a single document.
 */
export function useFirestoreDocumentListener<T>(
  ref: ERef<T> | undefined
): ESnapshotExists<T> | null {
  const [snap, setSnap] = useState<ESnapshotExists<T> | null>(null);

  useEffect(() => {
    if (!ref) {
      return setSnap(null);
    }

    const unsubscribe = ref.onSnapshot(
      next => setSnap(exists(next) ? next : null),
      err => {
        const firestoreError = err as FirestoreError;
        if (firestoreError.code === 'permission-denied') {
          getErrorReporter().logAndCaptureWarning(
            'Permission denied when listening to document snapshot',
            {
              ref: ref.path
            }
          );
          return;
        }

        getErrorReporter().logAndCaptureError(
          ColumnService.DATABASE,
          firestoreError,
          'Error listening to document snapshot',
          {
            ref: ref.path
          }
        );
      }
    );

    return unsubscribe;
  }, [ref?.id]);

  return snap;
}
